import { UsersIcon } from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { T } from "@/shared/i18n";
import { SALES_CONTACT_URL } from "@/shared/config/pricing";
import { useCompany } from "@/entities/company/hooks/use-company";
import { useIsOwner } from "@/entities/user/hooks/use-is-owner";
import { useSeatLimitDialogStore } from "../model/seat-limit-dialog-store";
import { seatLimitAction } from "../lib/seat-limit-action";
import { track } from "@/shared/lib/analytics";

/** The gated feature's key, as the upgrade dialog names the same limit. */
const SEAT_LIMIT_FEATURE = "users";

const SeatLimitBody = ({
  limit,
  onClose,
  onDismiss,
}: {
  limit: number;
  onClose: () => void;
  onDismiss: () => void;
}) => {
  const company = useCompany();
  const { isOwner } = useIsOwner();
  const action = seatLimitAction({
    canPurchaseAdditionalSeats: company?.canPurchaseAdditionalSeats ?? false,
    isOwner,
  });

  return (
    <>
      <DialogDescription className="text-center">
        {action === "add-seats" ? (
          <T
            id="All user seats on your plan are in use ({limit} of {limit}). Add a seat to invite another user."
            params={{ limit }}
          />
        ) : (
          <T
            id="All user seats on your plan are in use ({limit} of {limit}). Contact sales to add more seats to your account."
            params={{ limit }}
          />
        )}
      </DialogDescription>

      <div className="flex flex-col items-center gap-3">
        {action === "add-seats" && company ? (
          <Button asChild>
            <Link
              to="/$companySlug/settings/subscription"
              params={{ companySlug: company.slug }}
              onClick={onClose}
            >
              <T id="Add seats" />
            </Link>
          </Button>
        ) : (
          <Button asChild>
            <a
              href={SALES_CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                track("billing.sales.contacted", {
                  source: "seat_limit_dialog",
                  feature: SEAT_LIMIT_FEATURE,
                })
              }
            >
              <T id="Contact Sales" />
            </a>
          </Button>
        )}
        <Button variant="ghost" onClick={onDismiss}>
          <T id="Not now" />
        </Button>
      </div>
    </>
  );
};

export const SeatLimitDialog = () => {
  const { isOpen, limit, closeSeatLimitDialog } = useSeatLimitDialogStore();

  // "Add seats" closes through onClose instead: that is a conversion.
  const dismiss = () => {
    track("billing.paywall.dismissed", { feature: SEAT_LIMIT_FEATURE });
    closeSeatLimitDialog();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && dismiss()}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="items-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-2">
            <UsersIcon className="h-6 w-6 text-muted-foreground" />
          </div>
          <DialogTitle className="text-xl text-center">
            <T id="You've reached your seat limit" />
          </DialogTitle>
        </DialogHeader>

        <SeatLimitBody
          limit={limit}
          onClose={closeSeatLimitDialog}
          onDismiss={dismiss}
        />
      </DialogContent>
    </Dialog>
  );
};
